import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/authcontext";
import ProjectDetailView from "./ProjectDetailView";
import { useProjectDetail } from "../../hooks/useProjectDetail";
import StyledModalView from "../Modal/StyledModalView";

function ProjectDetailContainer() {
    const { id } = useParams();
    const { auth } = useAuth();
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [documentCategory, setDocumentCategory] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [hourlyRate, setHourlyRate] = useState("");
    const [meterRate, setMeterRate] = useState("");
    const [showAddEmployee, setShowAddEmployee] = useState(false);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [selectedRole, setSelectedRole] = useState("");
    const [editingEmployeeId, setEditingEmployeeId] = useState(null);
    const [newHourlyRate, setNewHourlyRate] = useState("");
    const [newMeterRate, setNewMeterRate] = useState("");
    const [newProjectRole, setNewProjectRole] = useState("");
    const [newRateType, setNewRateType] = useState("");
    const [rateType, setRateType] = useState("hourly");
    const [file, setFile] = useState(null);

    // State for document delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);

    const detail = useProjectDetail(id);

    // Get these from the detail hook
    const {
        project,
        setProject,
        editableProject,
        setEditableProject,
        isEditing,
        setIsEditing,
        profit,
        employees,
        availableEmployees,
        documents,
        assignments,
        loading,
        error,
        handleSaveClick,
        handleAddEmployee,
        handleEditEmployee,
        handleSaveEmployeeChanges,
        handleRemoveEmployee,
        handleDocumentUpload,
        handleDeleteDocument,
        handleCreateAssignment,
        handleUpdateAssignment,
        handleDeleteAssignment,
        totalEmployeeCosts,
        totalExtraMaterialCosts,
        ...detailRest
    } = detail;

    const { showFlash, ...detailRestWithoutShowFlash } = detailRest;

    const handleEditClick = () => setIsEditing(true);
    const handleCancelClick = () => {
        setIsEditing(false);
        setEditableProject(project);
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let parsedValue = value;

        // Parse numbers as floats to allow decimals
        if (["projectprice", "projectrate", "materialcosts"].includes(name)) {
            parsedValue = value === '' ? '' : parseFloat(value);
        }

        setEditableProject((prev) => ({ ...prev, [name]: parsedValue }));
    };

    const handleEditEmployeeWrapper = (employee) =>
        handleEditEmployee(
            employee,
            setEditingEmployeeId,
            setNewProjectRole,
            setNewRateType,
            setNewHourlyRate,
            setNewMeterRate
        );

    const handleAddEmployeeWrapper = (employee) =>
        handleAddEmployee(
            selectedEmployee,
            selectedRole,
            rateType,
            hourlyRate,
            meterRate,
            detail.fetchProjectDetails,
            showFlash,
            setShowAddEmployee,
            setSelectedEmployee,
            setSelectedRole,
            setHourlyRate,
            setMeterRate,
            setRateType,
        )

    // Wrapper for document upload to match the expected signature in the view
    const handleDocumentUploadWrapper = (e) =>
        handleDocumentUpload(
            e,
            file,
            documentCategory,
            expiresAt,
            detail.fetchProjectDetails,
            setFile,
            setDocumentCategory,
            setExpiresAt,
            setShowUploadForm
        );

    // Pass setEditingEmployeeId to handleSaveEmployeeChanges
    const handleSaveEmployeeChangesWrapper = (employeeId, newRateType, newMeterRate, newHourlyRate, newProjectRole) =>
        handleSaveEmployeeChanges(
            employeeId,
            newRateType,
            newMeterRate,
            newHourlyRate,
            newProjectRole,
            setEditingEmployeeId
        );

    // Handler to open modal
    const handleRequestDeleteDocument = (docId) => {
        setDocumentToDelete(docId);
        setShowDeleteModal(true);
    };
    // Handler to confirm delete
    const handleConfirmDeleteDocument = () => {
        if (documentToDelete) {
            handleDeleteDocument(documentToDelete);
        }
        setShowDeleteModal(false);
        setDocumentToDelete(null);
    };
    // Handler to cancel delete
    const handleCancelDeleteDocument = () => {
        setShowDeleteModal(false);
        setDocumentToDelete(null);
    };

    return (
        <>
            <ProjectDetailView
                project={project}
                setProject={setProject}
                editableProject={editableProject}
                setEditableProject={setEditableProject}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                profit={profit}
                employees={employees}
                availableEmployees={availableEmployees}
                documents={documents}
                assignments={assignments}
                loading={loading}
                error={error}
                handleSaveClick={handleSaveClick}
                handleAddEmployee={handleAddEmployeeWrapper}
                handleEditEmployee={handleEditEmployeeWrapper}
                handleSaveEmployeeChanges={handleSaveEmployeeChangesWrapper}
                handleRemoveEmployee={handleRemoveEmployee}
                handleDocumentUpload={handleDocumentUploadWrapper}
                handleDeleteDocument={handleRequestDeleteDocument}
                handleCreateAssignment={handleCreateAssignment}
                handleUpdateAssignment={handleUpdateAssignment}
                handleDeleteAssignment={handleDeleteAssignment}
                totalEmployeeCosts={totalEmployeeCosts}
                totalExtraMaterialCosts={totalExtraMaterialCosts}
                auth={auth}
                showAddEmployee={showAddEmployee}
                setShowAddEmployee={setShowAddEmployee}
                showUploadForm={showUploadForm}
                setShowUploadForm={setShowUploadForm}
                setSelectedEmployee={setSelectedEmployee}
                setSelectedRole={setSelectedRole}
                rateType={rateType}
                setRateType={setRateType}
                hourlyRate={hourlyRate}
                setHourlyRate={setHourlyRate}
                meterRate={meterRate}
                setMeterRate={setMeterRate}
                editingEmployeeId={editingEmployeeId}
                setEditingEmployeeId={setEditingEmployeeId}
                newHourlyRate={newHourlyRate}
                setNewHourlyRate={setNewHourlyRate}
                newMeterRate={newMeterRate}
                setNewMeterRate={setNewMeterRate}
                newProjectRole={newProjectRole}
                setNewProjectRole={setNewProjectRole}
                newRateType={newRateType}
                setNewRateType={setNewRateType}
                setFile={setFile}
                documentCategory={documentCategory}
                setDocumentCategory={setDocumentCategory}
                expiresAt={expiresAt}
                setExpiresAt={setExpiresAt}
                handleInputChange={handleInputChange}
                handleEditClick={handleEditClick}
                handleCancelClick={handleCancelClick}
                {...detailRestWithoutShowFlash}
            />
            <StyledModalView
                isOpen={showDeleteModal}
                onClose={handleCancelDeleteDocument}
                title="Document verwijderen?"
                closeText="Annuleren"
                confirmText="Verwijderen"
                onConfirm={handleConfirmDeleteDocument}
            >
                Weet je zeker dat je dit document wilt verwijderen?
            </StyledModalView>
        </>
    );
}

export default ProjectDetailContainer;

ProjectDetailContainer.propTypes = {
    // No props - uses useParams
};
